import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/hooks/useSeo";
import { Search, CalendarCheck, X, GraduationCap, Star, FileText } from "lucide-react";

const SUBJECTS = ["", "Math", "English", "Science", "Social Studies", "French",
                    "Physics", "Chemistry", "Biology", "Coding", "Reading"];

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-CA", {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function ParentDashboard() {
  useSeo({ title: "Parent Dashboard | Teacher4Me Learning Centre" });
  const { user, loading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [subject, setSubject] = useState("");
  const [mode, setMode] = useState("");
  const [slots, setSlots] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadProfile = async () => {
    try {
      const { data } = await api.get("/api/centre/my-profile");
      setProfile(data);
      if (data.students && data.students.length > 0 && !selectedStudent) {
        setSelectedStudent(data.students[0].user_id);
      }
    } catch (err) { toast.error("Profile load failed"); }
  };

  const loadSessions = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/api/centre/parent/sessions"),
        api.get("/api/centre/parent/reports"),
      ]);
      setSessions(a.data.items || []);
      setReports(b.data.items || []);
    } catch (err) {
      console.error("Parent session/report load failed", err);
    }
  };

  const searchSlots = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (subject) params.set("subject", subject);
      if (mode) params.set("mode", mode);
      const { data } = await api.get(`/api/centre/slots/available?${params.toString()}`);
      setSlots(data.items || []);
    } catch (err) {
      toast.error("Search failed");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (user && user.role === "parent") {
      loadProfile(); loadSessions(); searchSlots();
    }
    // eslint-disable-next-line
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "parent") return <Navigate to="/" replace />;

  const bookSlot = async (slotId) => {
    if (!selectedStudent) return toast.error("Pick a child first");
    try {
      await api.post("/api/centre/book", {
        slot_id: slotId, student_user_id: selectedStudent,
      });
      toast.success("Session booked!");
      await Promise.all([searchSlots(), loadSessions()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    }
  };

  const cancelSession = async (id) => {
    const reason = window.prompt("Reason for cancelling?");
    if (reason == null) return;
    try {
      await api.post(`/api/centre/sessions/${id}/cancel`, { reason });
      toast.success("Session cancelled");
      await Promise.all([searchSlots(), loadSessions()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  const students = profile?.students || [];
  const upcoming = sessions.filter((s) => s.status === "scheduled");

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="parent-dashboard">
      <div className="mb-6">
        <div className="label-eyebrow">Parent</div>
        <h1 className="font-display font-black text-3xl mt-1">
          Welcome{profile?.parent?.full_name ? `, ${profile.parent.full_name.split(" ")[0]}` : ""}
        </h1>
        <p className="text-sm text-[#4B5563] mt-1">
          Manage your child's sessions and book new ones with approved teachers.
        </p>
      </div>

      {/* Students */}
      <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6 mb-6">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-3">
          <GraduationCap className="w-5 h-5 text-[#D92D20]" /> Your children
        </h2>
        {students.length === 0 ? (
          <div className="text-sm text-[#4B5563]">No students yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {students.map((s) => (
              <button key={s.user_id} onClick={() => setSelectedStudent(s.user_id)}
                data-testid={`student-${s.user_id.slice(0, 8)}`}
                className={`text-left border rounded-lg p-3 transition-colors ${
                  selectedStudent === s.user_id
                    ? "bg-[#FEF2F2] border-[#D92D20]"
                    : "bg-white border-[#E5E7EB] hover:border-[#D92D20]"
                }`}>
                <div className="font-semibold text-sm">{s.full_name}</div>
                <div className="text-xs text-[#4B5563]">{s.grade} · {s.province}</div>
              </button>
            ))}
          </div>
        )}
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Available slots */}
        <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
          <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-4">
            <Search className="w-5 h-5 text-[#064E3B]" /> Find a session
          </h2>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <label className="text-xs font-semibold text-[#4B5563]">Subject</label>
              <select value={subject} onChange={(e) => setSubject(e.target.value)}
                data-testid="search-subject"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-2 py-2 text-sm bg-white">
                {SUBJECTS.map((s) => <option key={s} value={s}>{s || "Any"}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#4B5563]">Mode</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}
                data-testid="search-mode"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-2 py-2 text-sm bg-white">
                <option value="">Any</option>
                <option value="physical">physical</option>
                <option value="online">online</option>
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={searchSlots} disabled={busy} data-testid="search-btn" className="btn-primary w-full">
                {busy ? "..." : "Search"}
              </button>
            </div>
          </div>

          <ul className="divide-y border border-[#E5E7EB] rounded-md max-h-[26rem] overflow-auto">
            {slots.length === 0 && (
              <li className="p-3 text-sm text-[#4B5563]" data-testid="empty-slots">
                No open slots match. Try clearing filters.
              </li>
            )}
            {slots.map((s) => (
              <li key={s.id} className="p-3 flex items-center justify-between"
                data-testid={`slot-${s.id.slice(0, 8)}`}>
                <div className="text-sm">
                  <div className="font-semibold">{fmt(s.start_at)}</div>
                  <div className="text-xs text-[#4B5563]">
                    {s.subject || "Any"} · {s.mode} · with <b>{s.teacher_name}</b>
                  </div>
                </div>
                <button onClick={() => bookSlot(s.id)}
                  data-testid={`book-${s.id.slice(0, 8)}`}
                  disabled={!selectedStudent}
                  className="text-xs px-3 py-1.5 rounded-full bg-[#D92D20] text-white font-semibold disabled:opacity-40 hover:bg-[#B91C1C]">
                  Book
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Sessions */}
        <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
          <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-4">
            <CalendarCheck className="w-5 h-5 text-[#D92D20]" /> Upcoming sessions
          </h2>
          {upcoming.length === 0 && (
            <div className="text-sm text-[#4B5563]" data-testid="empty-upcoming">
              You have no upcoming sessions. Book one from the list on the left.
            </div>
          )}
          <ul className="divide-y border border-[#E5E7EB] rounded-md">
            {upcoming.map((s) => (
              <li key={s.id} className="p-4"
                data-testid={`parent-session-${s.id.slice(0, 8)}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-sm">{fmt(s.start_at)}</div>
                    <div className="text-xs text-[#4B5563]">
                      {s.subject || "—"} · {s.mode} · with {s.teacher_name}
                    </div>
                    <div className="text-sm mt-1">
                      For: <b>{s.student_name}</b> {s.student_grade ? `· ${s.student_grade}` : ""}
                    </div>
                  </div>
                  <button onClick={() => cancelSession(s.id)}
                    data-testid={`cancel-parent-session-${s.id.slice(0, 8)}`}
                    className="text-xs text-[#B91C1C] font-semibold flex items-center gap-1 hover:underline">
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Recent Reports */}
      <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6 mt-6"
               data-testid="reports-section">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-[#064E3B]" /> Recent session reports
        </h2>
        {reports.length === 0 && (
          <div className="text-sm text-[#4B5563]" data-testid="empty-reports">
            Reports from completed sessions will appear here so you always know what your child
            worked on and what's next.
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          {reports.map((s) => (
            <ReportCard key={s.id} session={s} />
          ))}
        </div>
      </section>
    </div>
  );
}

function ReportCard({ session }) {
  const r = session.report || {};
  const [open, setOpen] = useState(false);
  const rating = r.performance_rating || 0;
  return (
    <div className="border border-[#E5E7EB] rounded-xl p-4 bg-[#F9FAFB]"
         data-testid={`report-card-${session.id.slice(0, 8)}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs uppercase font-semibold text-[#4B5563] tracking-wider">
            {new Date(session.start_at).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
          </div>
          <div className="font-display font-bold mt-0.5">{session.student_name}</div>
          <div className="text-xs text-[#4B5563]">
            {session.subject || "—"} · with {session.teacher_name}
          </div>
        </div>
        <div className="flex" data-testid={`report-rating-${session.id.slice(0, 8)}`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n}
              className={`w-4 h-4 ${n <= rating ? "text-[#D92D20] fill-[#D92D20]" : "text-[#D1D5DB]"}`} />
          ))}
        </div>
      </div>
      <div className="mt-3 text-sm">
        <div className="font-semibold">Topic: <span className="font-normal">{r.topic_taught || "—"}</span></div>
        {r.next_topic && (
          <div className="text-xs text-[#4B5563] mt-1">Next: {r.next_topic}</div>
        )}
      </div>
      {open ? (
        <div className="mt-3 space-y-2 text-sm border-t border-[#E5E7EB] pt-3"
             data-testid={`report-expanded-${session.id.slice(0, 8)}`}>
          {r.exercises_completed && <ReportRow label="Exercises done" v={r.exercises_completed} />}
          {r.homework_assigned  && <ReportRow label="Homework"       v={r.homework_assigned} />}
          {r.strengths          && <ReportRow label="Strengths"       v={r.strengths} />}
          {r.weaknesses         && <ReportRow label="To work on"      v={r.weaknesses} />}
        </div>
      ) : null}
      <button onClick={() => setOpen(!open)}
        data-testid={`toggle-report-${session.id.slice(0, 8)}`}
        className="text-xs font-semibold text-[#D92D20] mt-3 hover:underline">
        {open ? "Hide details" : "Read full report →"}
      </button>
    </div>
  );
}

function ReportRow({ label, v }) {
  return (
    <div>
      <div className="text-xs uppercase font-semibold text-[#4B5563] tracking-wider">{label}</div>
      <div className="text-sm">{v}</div>
    </div>
  );
}
